import { useState } from "react";
import { katalogForm } from "../dienste/vorschlaege.js";
import { datumAusZeit } from "../kern/format.js";
import { Aenderungen } from "../ui/aenderungen.jsx";
import { Btn, Leer } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";

/* ================================================================
   Änderungen am Katalog — was dort passiert ist und was es für meine
   Objekte bedeutet.

   Oben stehen die offenen Abweichungen: Katalogänderungen an Objekten,
   die ich selbst anders führe. Sie liegen im Kellerbuch und bleiben,
   bis ich entscheide (dienste/historie.js). Darunter, was in dieser
   Sitzung eingespielt wurde — das ist Rückmeldung, keine Aufgabe.
   ================================================================ */

const TYP_LABEL = { wein: "Wein", winzer: "Winzer", region: "Region", katalog: "Katalog" };
const TYP_ZU_LISTE = { wein: "weine", winzer: "winzer", region: "regions" };

const AUSGANG = {
  eingespielt: { text: "übernommen", klasse: "ist-angenommen" },
  zusammengefuehrt: { text: "zusammengeführt", klasse: "ist-angenommen" },
  konflikt: { text: "abweichend", klasse: "ist-abgelehnt" },
  abgekoppelt: { text: "lokal geführt", klasse: "" },
  schonAktuell: { text: "bereits aktuell", klasse: "" },
  eigeneFassung: { text: "deine Fassung bleibt", klasse: "" },
  sonstige: { text: "", klasse: "" },
};

const artText = (e) => (e.art === "neu" ? t("neu aufgenommen")
  : e.art === "befuellung" ? t("Katalog befüllt")      /* nur noch in alten Protokollen */
    : e.art === "zusammenfuehrung" ? t("zusammengeführt") : t("geändert"));


function Abweichung({ a, db, namen, onUebernehmen, onBehalten }) {
  const [laeuft, setLaeuft] = useState(false);
  const eigen = (db[TYP_ZU_LISTE[a.typ]] || []).find((o) => o.id === a.objektId);
  return (
    <li className="wl-vs-zeile">
      <div className="wl-vs-kopf">
        <span className="wl-vs-name">{a.name || (eigen && eigen.name) || t("(ohne Namen)")}</span>
        <span className="wl-vs-status ist-abgelehnt">{t("abweichend")}</span>
      </div>
      <div className="wl-vs-meta">
        {t(TYP_LABEL[a.typ] || a.typ)}
        {a.durch ? ` · ${t("durch {n}", { n: a.durch })}` : ""} · {datumAusZeit(a.zeit)}
      </div>
      <p className="wl-vs-hinweis">{t("Deine Fassung → Katalogfassung")}</p>
      {eigen && (
        <Aenderungen vorher={katalogForm(a.typ, eigen)} nachher={a.nachher} namen={namen}
          konfliktFelder={a.konfliktFelder || []} leerText={t("Keine inhaltliche Änderung erkennbar.")} />
      )}
      <div className="wl-vs-aktionen wl-vs-aktionen--umbruch">
        <Btn variante="voll" disabled={laeuft}
          onClick={() => { setLaeuft(true); onUebernehmen(a.eintragId); }}>
          {t("Katalogfassung übernehmen")}
        </Btn>
        <Btn variante="linie" disabled={laeuft}
          onClick={() => { setLaeuft(true); onBehalten(a.eintragId); }}>
          {t("Meine behalten")}
        </Btn>
      </div>
    </li>
  );
}


function Historie({ db, abweichungen = [], ergebnisse = [], namen = {}, onUebernehmen, onBehalten, onClose }) {
  /* Was den Nutzer nicht betrifft, wird gar nicht erst gezeigt; offene
     Abweichungen stehen oben bereits mit ihrem aktuellen Stand. */
  const neueste = [...ergebnisse].reverse()
    .filter((r) => r.ausgang !== "fremd" && r.ausgang !== "konflikt");

  const zeile = (r, i) => {
    const e = r.eintrag;
    const a = AUSGANG[r.ausgang] || AUSGANG.sonstige;
    return (
      <li key={e.id || i} className="wl-vs-zeile">
        <div className="wl-vs-kopf">
          <span className="wl-vs-name">{e.name || t("(ohne Namen)")}</span>
          {a.text && <span className={"wl-vs-status " + a.klasse}>{t(a.text)}</span>}
        </div>
        <div className="wl-vs-meta">
          {t(TYP_LABEL[e.typ] || e.typ)} · {artText(e)}
          {e.durch ? ` · ${t("durch {n}", { n: e.durch })}` : ""} · {datumAusZeit(e.zeit)}
        </div>
        {e.art !== "befuellung" && e.art !== "zusammenfuehrung" && e.nachher && (
          <Aenderungen vorher={e.vorher ? katalogForm(e.typ, e.vorher) : null}
            nachher={katalogForm(e.typ, e.nachher)} namen={namen}
            ausblenden={e.art === "neu" ? ["name"] : []} />
        )}
        {r.ausgang === "zusammengefuehrt" && (
          <p className="wl-vs-hinweis">
            {t("Im Katalog wurden zwei Einträge vereint — deine Bestände und Verweise sind mitgewandert.")}
          </p>
        )}
        {r.ausgang === "eigeneFassung" && (
          <p className="wl-vs-hinweis">
            {t("Du hast seit deinem Vorschlag weiter daran gearbeitet. Deine neuere Fassung bleibt und geht als Änderung an den Verwalter.")}
          </p>
        )}
        {r.ausgang === "abgekoppelt" && (
          <p className="wl-vs-hinweis">{t("Dieses Objekt wird bei dir lokal geführt und nicht mehr abgeglichen.")}</p>
        )}
      </li>
    );
  };

  return (
    <div className="wl-teil">
      <p className="wl-teil-info">
        {t("Änderungen am gemeinsamen Katalog, die deine Weine, Winzer oder Regionen betreffen. Wo deine Fassung noch dem alten Stand entsprach, wurde still nachgezogen. Bestände, Lagerorte und Preise bleiben immer unberührt.")}
      </p>

      <span className="wl-konto-titel">{t("Zu entscheiden")} · {abweichungen.length}</span>
      {abweichungen.length === 0
        ? <Leer text={t("Nichts zu entscheiden.")} />
        : (
          <>
            <p className="wl-teil-info">
              {t("Hier hat der Katalog ein Feld geändert, das du selbst anders geändert hast. Alles Übrige ist bereits übernommen. „Meine behalten“ lässt deine Angabe stehen; sie geht dann als Änderungsvorschlag an den Verwalter.")}
            </p>
            <ul className="wl-vs-liste">
              {abweichungen.map((a) => (
                <Abweichung key={a.eintragId} a={a} db={db} namen={namen}
                  onUebernehmen={onUebernehmen} onBehalten={onBehalten} />
              ))}
            </ul>
          </>
        )}

      {neueste.length > 0 && (
        <>
          <div className="wl-teil-trenner" />
          <span className="wl-konto-titel">{t("Zuletzt eingespielt")}</span>
          <ul className="wl-vs-liste">{neueste.slice(0, 80).map(zeile)}</ul>
        </>
      )}

      <div className="wl-teil-fuss">
        <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );
}

export { Historie };
